import classNames from 'classnames'
import { FC } from 'react'
import { DeepDive } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'
import { DeltaBadge } from '../shared/Dashboard/DeltaBadge'
import { Pill } from '../shared/Pill'
import {
  formatCompact,
  formatPercent,
  formatPosition,
  SEVERITY_LABELS,
} from '../shared/utils'

const shareTone = (share: number) => {
  if (share >= 50) return styles.good
  if (share >= 25) return styles.warn
  return styles.bad
}

const models = (count: number, of: number) =>
  `${count}/${of} ${of === 1 ? 'Model' : 'Models'}`

const CheckCircle: FC = () => (
  <svg fill="none" height="18" viewBox="0 0 18 18" width="18">
    <circle cx="9" cy="9" r="7.5" stroke="#16a34a" strokeWidth="1.5" />
    <path
      d="M5.8 9.2l2.1 2.1 4.3-4.6"
      stroke="#16a34a"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const AlertCircle: FC = () => (
  <svg fill="none" height="18" viewBox="0 0 18 18" width="18">
    <circle cx="9" cy="9" r="7.5" stroke="#e5484d" strokeWidth="1.5" />
    <path
      d="M9 5.2v4.6M9 12.6v.1"
      stroke="#e5484d"
      strokeLinecap="round"
      strokeWidth="1.6"
    />
  </svg>
)

interface OutcomeSummaryProps {
  data: DeepDive
}

// WIN / LOSS with its severity and narrative, the five run tiles and whether
// the brand and its domain were mentioned. Shown for the latest run and for
// each Reload History run.
export const OutcomeSummary: FC<OutcomeSummaryProps> = ({ data }) => {
  const { outcome, tiles } = data
  const isWin = outcome?.result === 'win'
  const visibilityShare = tiles.visibility.of
    ? (tiles.visibility.count / tiles.visibility.of) * 100
    : 0
  const recommendationShare = tiles.recommendation.of
    ? (tiles.recommendation.count / tiles.recommendation.of) * 100
    : 0

  return (
    <div className={styles.outcomeBlock}>
      <div className={styles.outcomeCard}>
        {outcome && (
          <div
            className={classNames(styles.outcomeBox, {
              [styles.outcomeWin]: isWin,
            })}
          >
            <span className={styles.outcomeLabel}>Overall Outcome</span>
            <span className={styles.outcomeRow}>
              <span className={styles.outcomeResult}>
                {isWin ? 'WIN' : 'LOSS'}
              </span>
              <span
                className={classNames(
                  styles.severity,
                  styles[`severity_${outcome.severity}`]
                )}
              >
                {SEVERITY_LABELS[outcome.severity]}
              </span>
            </span>
          </div>
        )}
        <div className={styles.outcomeText}>
          <p className={styles.outcomePrompt}>“{data.prompt.prompt}”</p>
          {outcome?.narrative && (
            <p className={styles.outcomeNarrative}>{outcome.narrative}</p>
          )}
        </div>
      </div>

      <div className={styles.tiles}>
        <div className={classNames(styles.tile, styles.tileActive)}>
          <span className={styles.tileTitle}>AI Visibility</span>
          <span className={styles.tileRow}>
            <span className={styles.tileValue}>
              {models(tiles.visibility.count, tiles.visibility.of)}
            </span>
            <span className={shareTone(visibilityShare)}>
              {formatPercent(visibilityShare)}
            </span>
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileTitle}>Recommendation Rate</span>
          <span className={styles.tileRow}>
            <span className={styles.tileValue}>
              {models(tiles.recommendation.count, tiles.recommendation.of)}
            </span>
            <span className={shareTone(recommendationShare)}>
              {formatPercent(recommendationShare)}
            </span>
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileTitle}>Average Position</span>
          <span className={styles.tileRow}>
            <span className={styles.tileValue}>
              {formatPosition(tiles.avgPosition)}
            </span>
            {outcome && (
              <span className={isWin ? styles.good : styles.bad}>
                {isWin ? 'WIN' : 'LOSS'}
              </span>
            )}
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileTitle}>Owned Citation Rate</span>
          <span className={styles.tileRow}>
            <span className={styles.tileValue}>
              {formatPercent(tiles.ownedCitationRate)}
            </span>
            {tiles.ownedCitationRate === 0 && (
              <span className={styles.bad}>Citation Gap</span>
            )}
          </span>
        </div>
        <div className={styles.tile}>
          <span className={styles.tileTitle}>
            Audience Reach <Pill tone="purple">Estimated</Pill>
          </span>
          <span className={styles.tileRow}>
            <span className={styles.tileValue}>
              {formatCompact(tiles.reach.value)}
            </span>
            <DeltaBadge kind="percent" value={tiles.reach.delta} />
          </span>
        </div>
      </div>

      <div className={styles.mentionedRow}>
        <span className={styles.mentionedItem}>
          {data.brandMentioned ? <CheckCircle /> : <AlertCircle />}
          Brand Mentioned:{' '}
          <strong className={data.brandMentioned ? styles.good : styles.bad}>
            {data.brandMentioned ? 'Yes' : 'No'}
          </strong>
        </span>
        <span className={styles.mentionedItem}>
          {data.domainCited ? <CheckCircle /> : <AlertCircle />}
          Domain Mentioned{data.domain ? ` (${data.domain})` : ''}:{' '}
          <strong className={data.domainCited ? styles.good : styles.bad}>
            {data.domainCited ? 'Yes' : 'No'}
          </strong>
        </span>
      </div>
    </div>
  )
}
